import { c as createFocusTrap } from './focus-trap.esm-C-N_-G0z.js';

/**
 * `html.touch` (from `html-head-js.liquid`): mega menus use click only.
 * `html.no-touch`: hover (`mouseenter` / `mouseleave`).
 */
function useClickOnlyMegaNav() {
  return typeof window !== 'undefined' && document.documentElement.classList.contains('touch');
}

class HeaderNavigation extends HTMLElement {
  static #CLOSE_DELAY_MS = 150;

  /** @type {NodeListOf<HTMLElement>} */ #controllers = this.querySelectorAll(
    '[aria-controls^="header-navigation--"]'
  );
  /** @type {HTMLElement|null} */ #activeItem;
  /** @type {AbortController|null} */ #scrollController = null;
  /** @type {import('focus-trap').FocusTrap} */ #focusTrap;
  /** @type {ReturnType<typeof setTimeout>|null} */ #closeTimeout = null;

  connectedCallback() {
    this.#controllers.forEach((controller) => {
      controller.addEventListener('mouseenter', () => this.#handleMouseenter(controller));
      controller.addEventListener('mouseleave', (event) =>
        this.#handleMouseleave(event, controller)
      );
      controller.addEventListener('keydown', (event) => this.#handleKeydown(event, controller));
      controller.addEventListener(
        'click',
        (event) => this.#handleControllerClick(event, controller),
        {
          capture: true,
        }
      );
    });
  }

  disconnectedCallback() {
    this.#cancelClose();
  }

  /**
   * Click-only mode: first tap opens (no navigation); second tap follows `href` when already open.
   */
  #handleControllerClick(event, controller) {
    if (!useClickOnlyMegaNav()) return;

    const target = this.#resolveAriaControlsTarget(controller);
    if (!target) return;

    if (controller.getAttribute('aria-expanded') !== 'true') {
      event.preventDefault();
      if (this.#activeItem && this.#activeItem.controller !== controller) {
        this.#close(this.#activeItem.controller, this.#activeItem.target);
      }
      this.#open(controller, target);
    }
  }

  /**
   * @param {HTMLElement} controller
   * @returns {HTMLElement|null}
   */
  #resolveAriaControlsTarget(controller) {
    const elements = controller.ariaControlsElements;
    if (elements?.length) return elements[0];
    const id = controller.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  #handleMouseenter(controller) {
    if (useClickOnlyMegaNav()) return;

    this.#cancelClose();

    const target = this.#resolveAriaControlsTarget(controller);
    if (!target) return;

    if (this.#activeItem?.controller === controller) return;

    if (this.#activeItem) this.#close(this.#activeItem.controller, this.#activeItem.target);

    this.#open(controller, target);
  }

  #handleMouseleave(event, controller) {
    if (useClickOnlyMegaNav()) return;

    const target = this.#resolveAriaControlsTarget(controller);
    if (!target) return;

    const nextElement = event.relatedTarget;

    if (
      nextElement instanceof Element &&
      (controller.contains(nextElement) || target.contains(nextElement))
    ) {
      return this.#watchTargetMouseleave(controller, target);
    }

    this.#scheduleClose(controller, target);
  }

  #watchTargetMouseleave(controller, target) {
    target.addEventListener('mouseleave', () => this.#scheduleClose(controller, target), {
      once: true,
    });
  }

  #scheduleClose(controller, target) {
    this.#cancelClose();
    this.#closeTimeout = setTimeout(() => {
      this.#closeTimeout = null;

      if (this.#activeItem?.controller !== controller) return;

      if (controller.matches(':hover') || target.matches(':hover')) {
        this.#watchTargetMouseleave(controller, target);
        return;
      }

      this.#close(controller, target);
    }, HeaderNavigation.#CLOSE_DELAY_MS);
  }

  #cancelClose() {
    if (this.#closeTimeout === null) return;
    clearTimeout(this.#closeTimeout);
    this.#closeTimeout = null;
  }

  /**
   * Handle controller keydown
   * @param {KeyboardEvent} event
   * @param {HTMLElement} controller
   */
  #handleKeydown(event, controller) {
    if (event.code !== 'Space') return;

    event.preventDefault();

    const target = this.#resolveAriaControlsTarget(controller);
    if (!target) return;

    if (this.#activeItem?.controller === controller) {
      this.#close(controller, target);
      return;
    }

    if (this.#activeItem) this.#close(this.#activeItem.controller, this.#activeItem.target);

    this.#open(controller, target);

    const trap = createFocusTrap(target, {
      onDeactivate: () => this.#close(controller, target),
    });
    this.#focusTrap = trap;

    target.addEventListener(
      'transitionend',
      () => {
        if (this.#focusTrap === trap) trap.activate();
      },
      { once: true }
    );
  }

  #setNavOpen(isOpen) {
    this.closest('header-element')?.classList.toggle('nav-open', isOpen);
  }

  #open(controller, target) {
    this.#cancelClose();
    this.#activeItem = { controller, target };

    controller.ariaExpanded = 'true';
    target.ariaHidden = 'false';
    this.#setNavOpen(true);

    target.querySelector('mega-menu-element')?.expandDefaultPanel?.();

    this.#addScrollListener(controller, target);
  }

  #close(controller, target) {
    this.#cancelClose();

    if (this.#activeItem && this.#activeItem.controller !== controller) return;

    this.#activeItem = null;

    controller.ariaExpanded = 'false';
    target.ariaHidden = 'true';
    this.#setNavOpen(false);

    target.querySelector('mega-menu-element')?.collapseAll?.();

    this.#scrollController?.abort();
    this.#scrollController = null;

    if (this.#focusTrap) {
      this.#focusTrap.deactivate({ onDeactivate: null });
      this.#focusTrap = null;
    }
  }

  #addScrollListener(controller, target) {
    this.#scrollController?.abort();

    this.#scrollController = new AbortController();
    window.addEventListener('scroll', () => this.#close(controller, target), {
      once: true,
      signal: this.#scrollController.signal,
    });
  }
}

customElements.define('header-navigation', HeaderNavigation);
